import { type SaplingToolkit } from "@taquito/sapling";
import { CustomError } from "@umami/utils";

type ToolkitReturn<T extends (...args: any[]) => unknown> = Awaited<ReturnType<T>>;

import type {
  SaplingTransactionWorkerRequest,
  ShieldTransactionWorkerRequest,
  UnshieldTransactionWorkerRequest,
  WorkerRequest,
  WorkerResponse,
} from "./types";

const WORKER_TIMEOUT_MS = 60_000 * 4;

class SaplingWorkerService {
  private worker: Worker | null = null;

  private getWorker(): Worker {
    if (!this.worker) {
      this.worker = new Worker(new URL("../workers/saplingWorker.ts", import.meta.url), {
        type: "module",
      });
    }

    return this.worker;
  }

  private async sendRequest<T>(request: WorkerRequest): Promise<T> {
    const worker = this.getWorker();

    return new Promise<T>((resolve, reject) => {
      const handleMessage = (event: MessageEvent<WorkerResponse>) => {
        cleanup();

        if (event.data.type === "success") {
          resolve(event.data.payload as T);
        } else {
          reject(new CustomError(event.data.payload.message));
        }
      };

      const handleError = (event: ErrorEvent) => {
        cleanup();
        reject(event.error instanceof Error ? event.error : new Error(event.message));
      };

      // Drop sapling worker request if it takes longer than 60 seconds
      const timeoutId = window.setTimeout(() => {
        cleanup();
        reject(new Error("Sapling request timed out"));
      }, WORKER_TIMEOUT_MS);

      const cleanup = () => {
        worker.removeEventListener("message", handleMessage);
        worker.removeEventListener("error", handleError);
        window.clearTimeout(timeoutId);
      };

      worker.addEventListener("message", handleMessage);
      worker.addEventListener("error", handleError);
      worker.postMessage(request);
    });
  }

  async prepareShieldedTransaction({
    mnemonic,
    contractAddress,
    rpcUrl,
    transaction,
  }: ShieldTransactionWorkerRequest): Promise<
    ToolkitReturn<SaplingToolkit["prepareShieldedTransaction"]>
  > {
    return this.sendRequest({
      type: "prepareShieldedTransaction",
      payload: {
        mnemonic,
        contractAddress,
        rpcUrl,
        transaction,
      },
    });
  }

  async prepareSaplingTransaction({
    mnemonic,
    contractAddress,
    rpcUrl,
    transactions,
  }: SaplingTransactionWorkerRequest): Promise<
    ToolkitReturn<SaplingToolkit["prepareSaplingTransaction"]>
  > {
    return this.sendRequest({
      type: "prepareSaplingTransaction",
      payload: {
        mnemonic,
        contractAddress,
        rpcUrl,
        transactions,
      },
    });
  }

  async prepareUnshieldedTransaction({
    mnemonic,
    contractAddress,
    rpcUrl,
    transaction,
  }: UnshieldTransactionWorkerRequest): Promise<
    ToolkitReturn<SaplingToolkit["prepareUnshieldedTransaction"]>
  > {
    return this.sendRequest({
      type: "prepareUnshieldedTransaction",
      payload: {
        mnemonic,
        contractAddress,
        rpcUrl,
        transaction,
      },
    });
  }

  terminate(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }
}

export const saplingWorkerService = new SaplingWorkerService();
